import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { getUser } from '../../utils/auth';

// Helper function from flaggedbyalgorithm.jsx - needed for getPt5Reference
const getPt5Reference = (property) =>
  property?.refno ?? property?.pt5 ?? property?.pt_5 ?? property?.pt ?? "";

const DoNotReassessmentFormPage = () => {
  const { pin } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const agreeWithAiFromNav = location.state?.agreeWithAi;
  const user = getUser();
  const userId = user?.id;

  console.log("DoNotReassessmentFormPage rendered. PIN:", pin, "agreeWithAiFromNav:", agreeWithAiFromNav);

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [pt5Reference, setPt5Reference] = useState("");
  const [notReassessComment, setNotReassessComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showCommentRequiredError, setShowCommentRequiredError] = useState(false);
  const [showReasonRequiredError, setShowReasonRequiredError] = useState(false);
  const [showReasonStep, setShowReasonStep] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      if (!pin || !userId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await axios.get(`/inspector/algorithm-flagged/${userId}?pin=${pin}`);
        if (res.data && res.data.length > 0) {
          setSelectedProperty(res.data[0]);
          setComment(res.data[0].overall_explanation || "");
          setPt5Reference(getPt5Reference(res.data[0]));
        } else {
          toast.error("Property not found.");
          navigate('/inspector/flaggedbyalgorithm');
        }
      } catch (err) {
        console.error("Error fetching property for 'Do Not Reassess':", err);
        toast.error("Failed to load property details.");
        navigate('/inspector/flaggedbyalgorithm');
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [pin, userId, navigate]);

  const handleNotReassess = async () => {
    if (!comment.trim()) {
      setShowCommentRequiredError(true);
      setShowReasonStep(false);
      return;
    }
    if (!notReassessComment.trim()) {
      setShowReasonRequiredError(true);
      return;
    }

    if (!selectedProperty || submitting) return;

    setSubmitting(true);
    const toastId = toast.loading("Submitting 'Do not Reassess'...");

    try {
      // 1. Save algorithm flag response (agreeWithAi) FIRST, if user selected an option, with empty comment
      if (agreeWithAiFromNav !== undefined && agreeWithAiFromNav !== null) {
        const agreeValue = agreeWithAiFromNav === 'partial' ? 2 : (agreeWithAiFromNav ? 1 : 0);
        await axios.post('/inspector/algorithm-flag-response', {
          propertyId: selectedProperty.property_id || selectedProperty.id,
          inspectorId: user.id || user.userId,
          response: "",
          agreeWithAi: agreeValue,
        });
      }

      const finalExplanation = [
        comment.trim(),
        `Reason for not reassessing: ${notReassessComment.trim()}`,
      ].filter(Boolean).join("\n\n");

      // 2. Save the comment and the do-not-reassess reason together.
      if (finalExplanation) {
        await axios.post("/inspector/save-explanation", {
          property_id: selectedProperty.property_id,
          user_id: user.id || user.userId,
          overall_explanation: finalExplanation,
          row_explanations: {}, // No row explanations for "do not reassess"
        });
      }

      // 3. Update property status
      const formData = new FormData();
      formData.append("pin", selectedProperty.pin);
      formData.append("userId", user.id || user.userId);
      formData.append("reassess", "0"); // Mark as "do not reassess" -> goes to Not Resolved tab
      formData.append("status", "unresolved"); // Required for supervisor flow compatibility
      formData.append("propertyId", selectedProperty.property_row_id || selectedProperty.property_id);

      // Required fields updateProperty() needs — without these it nulls out these columns
      formData.append("circle", selectedProperty.circle || "");
      formData.append("occupationstatus", selectedProperty.occupationstatus || "");
      formData.append("location", selectedProperty.location || "");
      formData.append("admin_garv", selectedProperty.admin_garv || "");
      formData.append("exp_garv", selectedProperty.exp_garv || "");
      formData.append("floor_admin", selectedProperty.floor_admin || "");
      formData.append("landuse_admin", selectedProperty.landuse_admin || "");
      formData.append("landarea_admin", selectedProperty.landarea_admin || "");
      formData.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      formData.append("floors_ml", selectedProperty.floors_ml || "");
      formData.append("landuse_ml", selectedProperty.landuse_ml || "");
      formData.append("landarea_ml", selectedProperty.landarea_ml || "");
      formData.append("coveredarea_ml", selectedProperty.coveredarea_ml || "");
      formData.append("floors_pred", selectedProperty.floors_pred || "");
      formData.append("land_use_pred", selectedProperty.land_use_pred || "");
      formData.append("landarea_combined_pred", selectedProperty.landarea_combined_pred || "");
      formData.append("coveredarea_combined_pred", selectedProperty.coveredarea_combined_pred || "");
      formData.append("refno", pt5Reference || getPt5Reference(selectedProperty));

      await axios.put(`/properties/assign-supervisor-notfiltered/${selectedProperty.pin}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Property marked as 'Do not Reassess'!", { id: toastId });
      navigate('/inspector/not-resolved'); // Redirect after successful submission
    } catch (err) {
      console.error("Error submitting 'Do not Reassess':", err);
      toast.error(err.response?.data?.error || "Failed to mark as 'Do not Reassess'", { id: toastId });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Loading property details...</p>
      </div>
    );
  }

  if (!selectedProperty) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 items-center justify-center">
        <p className="text-gray-600">No property data available.</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-lg shadow-2xl flex flex-col max-w-md w-full relative"
      >
        <div className="flex-shrink-0 p-6 border-b border-gray-200 pr-14">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-10 flex items-center justify-center w-10 h-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h3 className="text-2xl font-bold text-gray-800">
            {showReasonStep ? "Reason for Not Reassessing" : "Property Comment and PT-5"}
          </h3>
          {selectedProperty.pin && <p className="text-gray-600 mt-1">PIN: {selectedProperty.pin}</p>}
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {showReasonStep ? (
            <div>
              <label htmlFor="not-reassess-reason" className="sr-only">
                Reason for not reassessing
              </label>
              <textarea
                id="not-reassess-reason"
                rows="4"
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                  showReasonRequiredError && !notReassessComment.trim()
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                value={notReassessComment}
                onChange={(e) => {
                  setNotReassessComment(e.target.value);
                  if (showReasonRequiredError && e.target.value.trim()) {
                    setShowReasonRequiredError(false);
                  }
                }}
                placeholder="Enter reason..."
                disabled={submitting}
              />
              {showReasonRequiredError && !notReassessComment.trim() && (
                <p className="mt-1 text-sm text-red-600">Reason is required</p>
              )}
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="not-reassess-comment" className="block text-sm font-medium text-gray-700 mb-1">
                  Comment <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="not-reassess-comment"
                  rows="4"
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm ${
                    showCommentRequiredError && !comment.trim()
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                  value={comment}
                  onChange={(e) => {
                    setComment(e.target.value);
                    if (showCommentRequiredError && e.target.value.trim()) {
                      setShowCommentRequiredError(false);
                    }
                  }}
                  placeholder="Enter comment..."
                  disabled={submitting}
                />
                {showCommentRequiredError && !comment.trim() && (
                  <p className="mt-1 text-sm text-red-600">Comment is required</p>
                )}
              </div>
              <div>
                <label htmlFor="not-reassess-pt5" className="block text-sm font-medium text-gray-700 mb-1">
                  PT-5 Serial Number <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="not-reassess-pt5"
                  type="text"
                  value={pt5Reference}
                  onChange={(e) => setPt5Reference(e.target.value)}
                  placeholder="Enter PT-5 Serial Number..."
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  disabled={submitting}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex-shrink-0 flex justify-end gap-4 p-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => {
              if (showReasonStep) {
                setShowReasonStep(false);
                setShowReasonRequiredError(false);
              } else {
                navigate(-1);
              }
            }}
            className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
            disabled={submitting}
          >
            {showReasonStep ? "Back" : "Cancel"}
          </button>
          <button
            onClick={() => {
              if (showReasonStep) {
                handleNotReassess();
                return;
              }
              if (!comment.trim()) {
                setShowCommentRequiredError(true);
                return;
              }
              setShowReasonStep(true);
            }}
            disabled={submitting || (showReasonStep && !notReassessComment.trim())}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
          >
            {submitting ? "Confirming..." : showReasonStep ? "Confirm" : "Next"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DoNotReassessmentFormPage;
